import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { withCommas, countUp } from "~/lib/web/counter";

const COUNT_MS = 1400;

export default class WebScatterController extends Controller<HTMLElement> {
  static override targets = ["tally"];
  declare readonly tallyTarget: HTMLElement;
  declare readonly hasTallyTarget: boolean;

  private observer: IntersectionObserver | null = null;
  private cancelCount: (() => void) | null = null;

  override connect(): void {
    if (prefersReducedMotion()) {
      // The figure is server-rendered at its final value, so no starting value
      // has to be written here.
      this.element.classList.add("is-shown");
      return;
    }

    // A section with no counter in its heading, which /mcp's prompts are, has
    // no tally target to read a starting value from.
    if (this.hasTallyTarget) this.write(this.countFrom);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.element.classList.add("is-shown");
        if (this.hasTallyTarget) {
          this.cancelCount = countUp(
            this.countFrom,
            this.countTo,
            COUNT_MS,
            (value) => this.write(value),
          );
        }
        this.observer?.disconnect();
      },
      /* Held until the section's top edge is a third of the way up the
         viewport. The tiles take about two seconds to arrive, so a trigger that
         fires before the section is on screen at all leaves nothing left to see
         by the time it is. */
      { threshold: 0, rootMargin: "0px 0px -35% 0px" },
    );
    this.observer.observe(this.element);
  }

  override disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.cancelCount?.();
    this.cancelCount = null;
  }

  private get countFrom(): number {
    return Number(this.tallyTarget.dataset["countFrom"] ?? 0);
  }

  private get countTo(): number {
    return Number(this.tallyTarget.dataset["countTo"] ?? 0);
  }

  private write(value: number): void {
    this.tallyTarget.textContent = `${withCommas(Math.round(value))}+`;
  }
}
